/**
 * Loss-aversion ad hooks (Sprint 2b): panic exit, rug insurance and trade
 * rewind buttons. HTML builders + shared ad flows, used by the coin detail
 * actions panel and the exchange screen. All buttons follow the
 * "fantasy main label + explicit ad sublabel" pattern.
 */
import { COINS, getCoinConfig } from '../game/config/coins.js';
import { INSURANCE_CONFIG } from '../game/config/risk.js';
import { getPanicQuote, getRewindQuote } from '../game/riskActions.js';
import type { GameState } from '../game/state.js';
import { formatNumber } from '../game/utils.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { AdManager, getAdRewards } from '../monetization/AdManager.js';
import { getState, dispatch, markScreenDirty, requestRender, showToast } from './renderer.js';

const shownHooks = new Set<string>();

function logShownOnce(hookId: string, key: string) {
  if (shownHooks.has(key)) return;
  shownHooks.add(key);
  dispatch({ type: 'AD_HOOK_LOG', payload: { hookId, event: 'shown' } });
}

function coinName(coinId: string): string {
  const cfg = COINS.find(c => c.id === coinId);
  return cfg ? t(cfg.nameKey) : coinId;
}

/** Panic exit button - shown while a crash is in progress and the coin is held. */
export function panicHookHtml(coinId: string, compact = false): string {
  const quote = getPanicQuote(getState(), coinId, Date.now());
  if (!quote) return '';
  logShownOnce('panic_sell', `panic_sell:${coinId}`);
  return `<button class="hook-btn hook-btn-panic${compact ? ' hook-btn-sm' : ''}" data-hook="panic" data-panic-coin="${coinId}">
    <span class="hook-main">${t('panic_btn')} +${formatNumber(quote.proceeds)}</span>
    <span class="hook-sub">${t('panic_sub')}</span>
  </button>`;
}

// === Rug insurance: offered for 10s right after a meme-coin buy ===
let insuranceOffer: { coinId: string; until: number } | null = null;

/** Arm the 10s insurance offer banner after a successful meme-coin buy. */
export function offerInsurance(coinId: string) {
  const cfg = getCoinConfig(coinId);
  if (!cfg || cfg.tier !== 'meme') return;
  const policy = getState().risk.insurance[coinId];
  if (policy && Date.now() < policy.expiresAt) return;
  insuranceOffer = { coinId, until: Date.now() + INSURANCE_CONFIG.offerDuration };
  dispatch({ type: 'AD_HOOK_LOG', payload: { hookId: 'rug_insurance', event: 'shown' } });
}

/** Active policy badge, or the offer banner while it lasts. */
export function insuranceHtml(coinId: string): string {
  const now = Date.now();
  const policy = getState().risk.insurance[coinId];
  if (policy && now < policy.expiresAt) {
    const remMin = Math.ceil((policy.expiresAt - now) / 60_000);
    return `<div class="cd-ins-active">${t('insurance_active')} · ${remMin}m</div>`;
  }
  if (!insuranceOffer || insuranceOffer.coinId !== coinId || now >= insuranceOffer.until) return '';
  const remSec = Math.ceil((insuranceOffer.until - now) / 1000);
  return `<button class="hook-btn hook-btn-ins" data-hook="insurance" data-ins-coin="${coinId}">
    <span class="hook-main">${t('insurance_btn')} (${remSec}s)</span>
    <span class="hook-sub">${t('insurance_sub')}</span>
  </button>`;
}

/** Trade rewind button - the last trade moved >=8% against the fill. */
export function rewindHookHtml(): string {
  const quote = getRewindQuote(getState(), Date.now());
  if (!quote || !quote.withinDisplay) return '';
  logShownOnce('trade_rewind', `trade_rewind:${quote.trade.at}`);
  const side = quote.trade.side === 'buy' ? t('ex_buy') : t('ex_sell');
  return `<button class="hook-btn hook-btn-rewind" data-hook="rewind">
    <span class="hook-main">${t('rewind_btn')} · ${coinName(quote.trade.coinId)} ${side} -${quote.adversePct.toFixed(1)}%</span>
    <span class="hook-sub">${t('rewind_sub')}</span>
  </button>`;
}

async function runPanicHook(coinId: string) {
  dispatch({ type: 'AD_HOOK_LOG', payload: { hookId: 'panic_sell', event: 'accepted' } });
  const result = await AdManager.showRewardedAd(getAdRewards().PANIC_SELL);
  if (result) {
    dispatch({ type: 'PANIC_SELL', payload: { coinId } });
    playSfx('golden');
    showToast(t('panic_success'));
  }
  markScreenDirty();
  requestRender();
}

async function runInsuranceHook(coinId: string) {
  dispatch({ type: 'AD_HOOK_LOG', payload: { hookId: 'rug_insurance', event: 'accepted' } });
  const result = await AdManager.showRewardedAd(getAdRewards().RUG_INSURANCE);
  if (result) {
    dispatch({ type: 'BUY_RUG_INSURANCE', payload: { coinId } });
    insuranceOffer = null;
    playSfx('achievement');
    showToast(t('insurance_active'));
  }
  markScreenDirty();
  requestRender();
}

async function runRewindHook() {
  dispatch({ type: 'AD_HOOK_LOG', payload: { hookId: 'trade_rewind', event: 'accepted' } });
  const result = await AdManager.showRewardedAd(getAdRewards().TRADE_REWIND);
  if (result) {
    dispatch({ type: 'TRADE_REWIND' });
    playSfx('golden');
    showToast(t('rewind_success'));
  }
  markScreenDirty();
  requestRender();
}

/** Bind panic/insurance/rewind buttons inside the container. */
export function bindRiskHooks(container: HTMLElement) {
  container.querySelectorAll('[data-hook="panic"]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      void runPanicHook((btn as HTMLElement).dataset.panicCoin!);
    });
  });
  container.querySelectorAll('[data-hook="insurance"]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      void runInsuranceHook((btn as HTMLElement).dataset.insCoin!);
    });
  });
  container.querySelectorAll('[data-hook="rewind"]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      void runRewindHook();
    });
  });
}

// === Insurance payout toast (fires once when the system pays out) ===
let lastPayoutSeen = 0;

/** Called from the main loop: toast newly arrived insurance payouts. */
export function checkRiskToasts(st: GameState) {
  const payout = st.risk?.lastInsurancePayout;
  if (!payout || payout.at === lastPayoutSeen) return;
  lastPayoutSeen = payout.at;
  if (Date.now() - payout.at > 5000) return; // stale (from a loaded save)
  showToast(`${t('insurance_payout')} +${formatNumber(payout.amount)} (${coinName(payout.coinId)})`);
  playSfx('golden');
}
